import type {
  FormatCatalogue,
  ModeLivraison,
  ReglagesLivraison,
  StatutPreparation,
  StatutReservation,
  TypeFormat,
  UniteTarif,
  WalletPhoto,
} from '@/types/photographe';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

/**
 * Adresse complète d'un chemin d'API (`/api/…`).
 *
 * Les aperçus sont servis par l'API, pas par le front : une balise `<img>` ou
 * une publication du fil a besoin de l'adresse entière.
 */
export const urlApi = (chemin: string) => `${API_URL}${chemin}`;

export const formatFcfa = (montant: number) => `${montant.toLocaleString('fr-FR')} F CFA`;

export const UNITES: Record<UniteTarif, string> = {
  match: 'le match',
  mi_temps: 'la mi-temps',
  heure: "l'heure",
  pack: 'le pack',
};

export const WALLETS_PHOTO: Record<WalletPhoto, { label: string; sigle: string; teinte: string; fond: string }> = {
  orange_money: { label: 'Orange Money', sigle: 'OM', teinte: '#FF7900', fond: '#FFF4EB' },
  wave: { label: 'Wave', sigle: 'W', teinte: '#1DC8FF', fond: '#EAF9FF' },
};

export const STATUTS_RESERVATION: Record<StatutReservation, { label: string; classes: string }> = {
  demandee: { label: 'En attente', classes: 'bg-amber-100 text-amber-800' },
  acceptee: { label: 'Acceptée', classes: 'bg-sky-100 text-sky-800' },
  refusee: { label: 'Refusée', classes: 'bg-gray-100 text-gray-600' },
  annulee: { label: 'Annulée', classes: 'bg-gray-100 text-gray-600' },
  terminee: { label: 'Livrée', classes: 'bg-emerald-100 text-emerald-800' },
};

export const TYPES_FORMAT: Record<TypeFormat, { label: string; court: string; description: string }> = {
  numerique: { label: 'Numérique', court: 'Fichier HD', description: 'Téléchargement immédiat, pleine résolution, sans filigrane' },
  tirage: { label: 'Tirage', court: 'Tirage photo', description: 'Impression sur papier photo, livrée chez vous' },
  encadre: { label: 'Encadré', court: 'Photo encadrée', description: 'Tirage sous cadre, prêt à accrocher' },
  toile: { label: 'Toile', court: 'Tableau sur toile', description: 'Impression sur toile tendue sur châssis' },
};

/** Un objet à imprimer et à remettre : tout sauf le fichier. */
export const estPhysique = (type: TypeFormat) => type !== 'numerique';

export const MODES_LIVRAISON: Record<ModeLivraison, { label: string; detail: string }> = {
  dakar: { label: 'Livraison à Dakar', detail: 'À votre adresse, dans Dakar' },
  autres_villes: { label: 'Autres villes', detail: 'Envoi dans une autre ville du Sénégal' },
  retrait: { label: 'Retrait sur place', detail: 'Chez le photographe, gratuit' },
};

export const STATUTS_PREPARATION: Record<StatutPreparation, { label: string; classes: string }> = {
  preparation: { label: 'En préparation', classes: 'bg-amber-100 text-amber-800' },
  expediee: { label: 'Expédiée', classes: 'bg-sky-100 text-sky-800' },
  livree: { label: 'Livrée', classes: 'bg-emerald-100 text-emerald-800' },
  retiree: { label: 'Retirée', classes: 'bg-emerald-100 text-emerald-800' },
};

/**
 * Catalogue proposé à l'ouverture de la boutique. Les prix ne sont que des
 * suggestions ; les objets physiques restent désactivés tant que le
 * photographe ne les a pas choisis (il doit d'abord pouvoir les imprimer).
 */
export const FORMATS_SUGGERES: Array<Omit<FormatCatalogue, 'id'>> = [
  { type: 'numerique', libelle: 'Fichier numérique HD', dimensions: '', prix: 2000, actif: true },
  { type: 'tirage', libelle: 'Tirage 20×30', dimensions: '20×30 cm', prix: 5000, actif: false },
  { type: 'encadre', libelle: 'Encadré 30×40', dimensions: '30×40 cm', prix: 15000, actif: false },
  { type: 'toile', libelle: 'Toile 40×60', dimensions: '40×60 cm', prix: 25000, actif: false },
];

export const LIVRAISON_VIDE: ReglagesLivraison = {
  dakar: { active: false, frais: 0 },
  autres_villes: { active: false, frais: 0 },
  retrait: { active: false, adresse: '' },
};

/** Initiales d'un nom affiché : « Awa Ndiaye » → « AN ». */
export function initiales(nom: string): string {
  return (
    nom
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((m) => m[0]!.toUpperCase())
      .join('') || '?'
  );
}

/** Les 9 chiffres d'un mobile sénégalais, quelle que soit l'écriture ; `null` sinon. */
export function mobileSenegalais(saisie: string): string | null {
  let chiffres = saisie.replace(/\D/g, '');
  if (chiffres.startsWith('00221')) chiffres = chiffres.slice(5);
  else if (chiffres.startsWith('221') && chiffres.length === 12) chiffres = chiffres.slice(3);
  return /^7\d{8}$/.test(chiffres) ? chiffres : null;
}

/** « 771234567 » → « 77 123 45 67 », au fil de la saisie. */
export function formaterNumero(saisie: string): string {
  const chiffres = saisie.replace(/\D/g, '').replace(/^(00)?221/, '').slice(0, 9);
  return [chiffres.slice(0, 2), chiffres.slice(2, 5), chiffres.slice(5, 7), chiffres.slice(7, 9)]
    .filter(Boolean)
    .join(' ');
}

/** Message d'erreur lisible d'une réponse d'API. */
export function messageErreur(erreur: unknown, repli = 'Une erreur est survenue.'): string {
  const data = (erreur as { response?: { data?: { message?: string; errors?: string[] } } })?.response?.data;
  return data?.errors?.[0] ?? data?.message ?? repli;
}
